import { formatDate } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { BehaviorSubject, firstValueFrom, Subject, takeUntil } from 'rxjs';

import { TwcPopupComponent, TwcValidators } from '@topcon/twc-angular';

import { LayerService, MagnetService, ProjectService, ToastService, TranslationService } from '@mirage-projects/cw-commons';
import { Layer, ProjectData, ProjectType } from '@mirage-projects/cw-models';

import { KeyValue } from '../../models/key-value';

type EditForm = FormGroup<{
    name: FormControl<string>;
    description: FormControl<string>;
    horizProjectionEPSG: FormControl<string>;
    geoid: FormControl<string>;
    captureDate: FormControl<Date>;
}>;

/**
 * Component that shows the edit project / layer dialog
 */
@Component({
    selector: 'cw-object-edit',
    templateUrl: './object-edit.component.html',
    styleUrls: ['./object-edit.component.scss'],
    standalone: false,
})
export class ObjectEditComponent implements OnInit, OnDestroy {
    /** The object to edit */
    @Input() object!: ProjectData | Layer;

    @Input() type: 'project' | 'layer' = 'project';

    @ViewChild('popup') popup!: TwcPopupComponent;

    editForm!: EditForm;
    nameDescMaxLength: 255 | typeof this.siteMaxLength = 255;

    private readonly isLoadingSubject$ = new BehaviorSubject(false);
    readonly isLoading$ = this.isLoadingSubject$.asObservable();

    readonly today = new Date();

    private onDestroy$: Subject<void> = new Subject();
    private readonly siteMaxLength = 63;

    constructor(
        private magnetService: MagnetService,
        private projectService: ProjectService,
        private toastService: ToastService,
        private translationService: TranslationService,
        private layerService: LayerService
    ) {}

    ngOnInit(): void {
        const isSiteProject = this.isProject() && this.magnetService.isSite() && this.object.projectType === ProjectType.SCAN;
        this.nameDescMaxLength = isSiteProject ? this.siteMaxLength : this.nameDescMaxLength;

        if (!this.object.name) {
            throw new Error('Object must have a name');
        }

        this.editForm = new FormGroup({
            name: new FormControl<string>(this.object.name, {
                validators: [Validators.required, Validators.maxLength(this.nameDescMaxLength)],
                nonNullable: true,
            }),
            description: new FormControl<string>(this.object.description ?? '', {
                validators: [Validators.maxLength(this.nameDescMaxLength)],
                nonNullable: true,
            }),
            horizProjectionEPSG: new FormControl<string>(
                {
                    value: this.object.horizProjectionEPSG ?? '',
                    disabled: !this.canEditCoordinate,
                },
                { nonNullable: true }
            ),
            geoid: new FormControl<string>(
                {
                    value: this.isProject() ? (this.object.geoid ?? '') : '',
                    disabled: !this.isProject(),
                },
                { nonNullable: true }
            ),
            captureDate: new FormControl<Date>(
                {
                    value: this.isProject() && this.object.captureDate ? new Date(this.object.captureDate) : this.today,
                    disabled: !this.isProject(),
                },
                { validators: [Validators.required, TwcValidators.maxDate(this.today)], nonNullable: true }
            ),
        });

        this.initLoadingSubscriptions();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
    }

    /**
     * Checks if we are working on a project object
     */
    isProject(): this is this & { object: ProjectData } {
        return this.type === 'project';
    }

    /**
     * Checks if we are working on a layer object
     */
    isLayer(): this is this & { object: Layer } {
        return this.type === 'layer';
    }

    /**
     * Set the selected coordinate reference system
     * @param value Selected CRS
     */
    setSelectedCRS(value: KeyValue): void {
        this.editForm.controls['horizProjectionEPSG'].markAsDirty();
        this.editForm.patchValue({
            horizProjectionEPSG: value?.key ? `EPSG:${value?.key}` : '',
        });
    }

    /**
     * Set the selected geoid
     * @param value Selected geoid
     */
    setSelectedGeoid(value: KeyValue): void {
        this.editForm.controls['geoid'].markAsDirty();
        this.editForm.patchValue({
            geoid: value?.key ?? '',
        });
    }

    /**
     * Close dialog
     */
    close(): void {
        this.popup.onClose();
    }

    /**
     * Save dialog data
     */
    async update(): Promise<void> {
        if (this.isProject()) {
            return await this.updateProject();
        } else if (this.isLayer()) {
            return await this.updateLayer();
        }
        throw new Error('Not supported object type');
    }

    /**
     * Check if coordinate system is editable
     */
    get canEditCoordinate(): boolean {
        return !this.magnetService.isSite();
    }

    /**
     * Check if capture date is editable
     */
    get canEditCaptureDate(): boolean {
        return !this.magnetService.isSite() && this.isProject();
    }

    /**
     * Check if geoid is editable
     * returns always false since COLW-12817 hide geoid
     */
    get canEditGeoid(): boolean {
        return false;
    }

    /**
     * Get dialog title
     */
    get caption(): string {
        if (this.isProject()) {
            return 'PROJECTS.EDIT';
        } else if (this.isLayer()) {
            return 'LAYERS.EDIT';
        }
        throw new Error('Not supported object type');
    }

    get nameRegex(): RegExp | string {
        const defaultPattern = '[\\s\\S]*';
        const sitePattern = /^[^:*?!"#$%&',.;<=>@^`|~¥/\\ \p{Extended_Pictographic}]*$/u;
        const isScanSiteProject = this.magnetService.isSite() && this.isProject() && this.isScanProject(this.object.projectType);

        return isScanSiteProject ? sitePattern : defaultPattern;
    }

    private initLoadingSubscriptions(): void {
        this.isLoading$.pipe(takeUntil(this.onDestroy$)).subscribe((isLoading) => {
            const action = isLoading ? 'disable' : 'enable';
            this.editForm.controls.name[action]();
            this.editForm.controls.description[action]();
            this.editForm.controls.captureDate[action]();
        });
    }

    private getProjectData(): Partial<ProjectData> {
        const formData = this.editForm.value;
        const captureDate = formData.captureDate
            ? { captureDate: formatDate(formData.captureDate, 'YYYY-MM-dd', this.translationService.currentLang) }
            : {};
        const data = {
            ...(this.editForm.value as Omit<ProjectData, 'captureDate'>),
            ...captureDate,
        };
        return data;
    }

    private isScanProject(projectType: ProjectType | undefined): boolean {
        return projectType === ProjectType.SCAN;
    }

    private async updateProject(): Promise<void> {
        const projectData = this.getProjectData();

        this.isLoadingSubject$.next(true);

        try {
            if (!this.object.id) {
                throw new Error('Object must have an id');
            }
            await firstValueFrom(this.projectService.update(this.object.id, projectData));
            this.close();
        } catch (error: unknown) {
            this.toastService.error((error as Error).message);
        } finally {
            this.isLoadingSubject$.next(false);
        }
    }

    private async updateLayer(): Promise<void> {
        const layerData = {
            ...this.object,
            ...this.editForm.value,
        } as Layer;

        this.isLoadingSubject$.next(true);

        try {
            if (!this.object.id) {
                throw new Error('Object must have an id');
            }
            await firstValueFrom(this.layerService.update(layerData));
            this.close();
        } catch (error: unknown) {
            this.toastService.error((error as Error).message);
        } finally {
            this.isLoadingSubject$.next(false);
        }
    }
}
